import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Check, X, Clock, Award, TrendingUp, Brain } from 'lucide-react';
import { toast } from 'sonner';

const ExamReportModal = ({ submission, open, onClose }) => {
  if (!submission) return null;

  const handleDownloadPDF = () => {
    toast.info('PDF download feature coming soon!');
  };

  const correctCount = submission.answers?.filter(a => a.is_correct).length || 0;
  const totalQuestions = submission.answers?.length || 0;
  const percentage = submission.percentage || 0;
  const totalScore = submission.total_score ?? 0;
  const maxScore = percentage > 0 ? Math.round(totalScore / (percentage / 100)) : totalQuestions;

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {submission.exam_title || 'Exam'} - Results
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="detailed">Detailed Results</TabsTrigger>
            <TabsTrigger value="insights">AI Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-6 mt-6">
            {/* Score Card */}
            <Card className="p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50">
              <div className="mb-4">
                <Award className={`w-16 h-16 mx-auto ${getGradeColor(percentage)}`} />
              </div>
              <h2 className={`text-5xl font-bold mb-2 ${getGradeColor(percentage)}`}>
                {percentage.toFixed(1)}%
              </h2>
              <p className="text-lg text-gray-600">
                {totalScore} / {maxScore} points
              </p>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Correct</p>
                    <p className="text-2xl font-bold text-green-600">{correctCount}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <X className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Incorrect</p>
                    <p className="text-2xl font-bold text-red-600">{totalQuestions - correctCount}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Taken</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatTime(submission.time_taken_seconds)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Progress Breakdown */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Performance Breakdown</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Correct Answers</span>
                    <span>{correctCount}/{totalQuestions}</span>
                  </div>
                  <Progress value={totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0} className="h-2 bg-green-200" />
                </div>
              </div>
            </Card>

            {/* Teacher Feedback */}
            {submission.teacher_feedback && (
              <Card className="p-6 bg-yellow-50 border-yellow-200">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-600" />
                  Teacher Feedback
                </h3>
                <p className="text-gray-700">{submission.teacher_feedback}</p>
                {submission.feedback_date && (
                  <p className="text-sm text-gray-500 mt-2">
                    {new Date(submission.feedback_date).toLocaleString()}
                  </p>
                )}
              </Card>
            )}
          </TabsContent>

          <TabsContent value="detailed" className="space-y-4 mt-6">
            {submission.answers?.map((answer, idx) => (
              <Card key={idx} className={`p-6 ${answer.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    answer.is_correct ? 'bg-green-200' : 'bg-red-200'
                  }`}>
                    {answer.is_correct ? (
                      <Check className="w-6 h-6 text-green-700" />
                    ) : (
                      <X className="w-6 h-6 text-red-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold">Question {idx + 1}</h4>
                      <Badge variant={answer.is_correct ? 'default' : 'destructive'}>
                        {answer.points_earned ?? 0} / {answer.max_points ?? 0} pts
                      </Badge>
                    </div>
                    <p className="text-gray-700 mb-3">{answer.question_text}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2">
                        <span className="font-medium">Your Answer:</span>
                        <span className={answer.is_correct ? 'text-green-700' : 'text-red-700'}>
                          {answer.student_answer || 'No answer'}
                        </span>
                      </div>
                      {!answer.is_correct && (
                        <div className="flex gap-2">
                          <span className="font-medium">Correct Answer:</span>
                          <span className="text-green-700">{answer.correct_answer}</span>
                        </div>
                      )}
                      {answer.explanation && (
                        <div className="mt-2 p-3 bg-blue-50 rounded">
                          <span className="font-medium text-blue-900">Explanation: </span>
                          <span className="text-blue-800">{answer.explanation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 mt-6">
            {submission.ai_insights ? (
              <>
                <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                    <h3 className="text-xl font-semibold">AI Performance Analysis</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Badge className="mb-2">
                        {submission.ai_insights.performance_level || 'good'}
                      </Badge>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {submission.ai_insights.full_analysis || submission.ai_insights.summary}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Performance Level
                  </h4>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={`text-lg px-4 py-1 ${
                        percentage >= 90 ? 'bg-green-500' :
                        percentage >= 75 ? 'bg-blue-500' :
                        percentage >= 60 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                    >
                      {percentage >= 90 ? 'Excellent' :
                       percentage >= 75 ? 'Good' :
                       percentage >= 60 ? 'Satisfactory' :
                       'Needs Improvement'}
                    </Badge>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-gray-600">AI insights not available for this submission</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <div className="flex gap-2">
            <Button onClick={handleDownloadPDF}>
              Download PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExamReportModal;
